import { usePageMeta } from "@/lib/usePageMeta";
import { useApi } from "@/lib/api";
import { toEvent, toSlide } from "@/lib/mappers";
import { SubscribeButton } from "@/components/site/SubscribeButton";
import { FeaturedCarousel } from "@/components/site/FeaturedCarousel";
import { EventListItem } from "@/components/site/EventCard";
import { SmartImage, Swappable, HeroBackdrop } from "@/components/site/Media";
import { NetworkGraphic, IllustrativeBadge, HeroNetwork } from "@/components/site/Brand";
import { useCopy } from "@/lib/copy";
import { Rich } from "@/components/site/Rich";
import { SectionHeading, ArrowLink, ButtonLink, Eyebrow, EmptyState, LoadingState, ErrorState } from "@/components/site/Content";

const Intro = ({ t }) => (
  <section className="relative overflow-hidden bg-brand text-white" data-testid="home-intro">
    <HeroBackdrop src={t("home.hero.bgImage")}><HeroNetwork className="pointer-events-none absolute inset-0 h-full w-full opacity-50 md:opacity-100" /></HeroBackdrop>
    <div className="container-x relative grid gap-10 py-16 md:py-24 lg:grid-cols-12">
      <div className="lg:col-span-8">
        <Eyebrow dark className="fade-up mb-5">{t("home.hero.eyebrow")}</Eyebrow>
        <h1 className="fade-up text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }} data-testid="home-hero-headline">
          {t("home.hero.headline")}
        </h1>
      </div>
      <div className="fade-up flex flex-col justify-end gap-6 lg:col-span-4" style={{ animationDelay: "160ms" }}>
        <p className="text-base leading-relaxed text-white/80 md:text-lg" data-testid="home-intro-text">
          <Rich text={t("home.hero.text")} />
        </p>
        <div className="flex flex-wrap gap-3">
          <ButtonLink to="/about" variant="green" testId="home-intro-about-button">{t("home.hero.ctaAbout")}</ButtonLink>
          <ButtonLink to="/research" variant="light" testId="home-intro-research-button">{t("home.hero.ctaStories")}</ButtonLink>
          <ButtonLink to="/events" variant="light" testId="home-intro-events-button">{t("home.hero.ctaEvents")}</ButtonLink>
        </div>
      </div>
    </div>
  </section>
);

const StoriesSlider = ({ slides, loading, error, t }) => (
  <section className="container-x pb-4 pt-6 md:pt-8" data-testid="home-stories">
    {loading ? <LoadingState className="h-[540px]" label="Loading stories" testId="carousel-loading" /> : error ? <ErrorState testId="carousel-error" /> : (
      <FeaturedCarousel slides={slides} heading={t("home.carousel.heading")} headingAction={<SubscribeButton compact label={t("home.carousel.subscribe")} href={t("contact.social.prasaraUrl")} testId="home-subscribe-button" />} action={<ArrowLink to="/research" testId="home-all-stories-link">{t("home.carousel.link")}</ArrowLink>} />
    )}
  </section>
);

const Visualiser = ({ t }) => (
  <section className="bg-brand text-white" aria-labelledby="visualiser-heading" data-testid="home-visualiser">
    <div className="container-x grid items-center gap-12 py-16 md:py-24 lg:grid-cols-2">
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <Eyebrow dark>{t("home.visualiser.eyebrow")}</Eyebrow>
          {t("home.visualiser.badge") && <IllustrativeBadge tone="dark" label={t("home.visualiser.badge")} />}
        </div>
        <h2 id="visualiser-heading" className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl">{t("home.visualiser.title")}</h2>
        <p className="mt-2 text-lg font-bold text-brand-green" data-testid="home-visualiser-subtitle">{t("home.visualiser.subtitle")}</p>
        <p className="mt-5 max-w-lg leading-relaxed text-white/80"><Rich text={t("home.visualiser.text")} /></p>
        {t("home.visualiser.note") && <p className="mt-3 max-w-lg whitespace-pre-line text-sm leading-relaxed text-white/60">{t("home.visualiser.note")}</p>}
        <div className="mt-8"><ButtonLink to="/repository" variant="green" testId="home-visualiser-link">{t("home.visualiser.button")}</ButtonLink></div>
      </div>
      <div className="relative">
        <Swappable src={t("home.visualiser.image")} alt={t("home.visualiser.imageAlt")} className="w-full object-contain"><NetworkGraphic className="w-full" animated /></Swappable>
        {t("home.visualiser.caption") && <p className="mt-3 text-xs text-white/60">{t("home.visualiser.caption")}</p>}
      </div>
    </div>
  </section>
);

const EventsTeaser = ({ upcoming, loading, error, t }) => (
  <section className="container-x py-16 md:py-24" aria-labelledby="events-heading" data-testid="home-events">
    <SectionHeading id="events-heading" eyebrow={t("home.events.eyebrow")} title={t("home.events.title")} intro={t("home.events.intro")} action={<ArrowLink to="/events" testId="home-all-events-link">{t("home.events.link")}</ArrowLink>} />
    {loading ? (
      <LoadingState className="h-64" label="Loading events" />
    ) : error ? (
      <ErrorState testId="home-events-error" />
    ) : upcoming.length ? (
      <div className="grid gap-x-12 md:grid-cols-2">
        {upcoming.map((e) => <EventListItem key={e.slug} event={e} />)}
      </div>
    ) : (
      <EmptyState title="No upcoming events" message="New events will appear here once they are listed." testId="home-events-empty" />
    )}
  </section>
);

const AboutMission = ({ t }) => (
  <section className="container-x py-16 md:py-24" aria-label="The Water Mission">
    <div className="grid overflow-hidden bg-pale sm:grid-cols-2" data-testid="home-water-mission">
      <SmartImage src={t("home.water.image")} alt={t("home.water.imageAlt")} ratio="aspect-[4/3] sm:aspect-[16/10]" imgClassName="object-[50%_75%]" />
      <div className="flex flex-col justify-between gap-8 p-8">
        <div>
          <Eyebrow>{t("home.water.eyebrow")}</Eyebrow>
          <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-brand">{t("home.water.title")}</h2>
          <p className="mt-4 text-sm leading-relaxed text-ink-muted"><Rich text={t("home.water.text")} /></p>
        </div>
        <ArrowLink to="/water-mission" testId="home-water-mission-link">{t("home.water.link")}</ArrowLink>
      </div>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(null, "BRaIN, the Bengaluru Research and Innovation Network, connects researchers, institutions, industry, startups, government and investors to turn Bengaluru research and innovation into impact.");
  const t = useCopy();
  const { data, loading, error } = useApi("/home");
  const slides = (data?.slides || []).map(toSlide);
  const upcoming = (data?.events || []).map(toEvent);
  return (
    <>
      <Intro t={t} />
      <AboutMission t={t} />
      <StoriesSlider slides={slides} loading={loading} error={error} t={t} />
      <EventsTeaser upcoming={upcoming} loading={loading} error={error} t={t} />
      <Visualiser t={t} />
    </>
  );
}
